<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import FunnelAPI from 'dashboard/api/funnels';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const getters = useStoreGetters();
const { t } = useI18n();

// Kanban action types with i18n keys
const KANBAN_ACTIONS = [
  { key: 'add_to_funnel', label: t('AUTOMATION.ACTION.KANBAN.ADD_TO_KANBAN') },
  {
    key: 'remove_from_funnel',
    label: t('AUTOMATION.ACTION.KANBAN.REMOVE_FROM_KANBAN'),
  },
  { key: 'move_to_stage', label: t('AUTOMATION.ACTION.KANBAN.MOVE_TO_STAGE') },
  {
    key: 'move_to_next_stage',
    label: t('AUTOMATION.ACTION.KANBAN.MOVE_TO_NEXT_STAGE'),
  },
  {
    key: 'move_to_previous_stage',
    label: t('AUTOMATION.ACTION.KANBAN.MOVE_TO_PREVIOUS_STAGE'),
  },
  {
    key: 'assign_funnel_agent',
    label: t('AUTOMATION.ACTION.KANBAN.ASSIGN_AGENT'),
  },
  {
    key: 'remove_funnel_agent',
    label: t('AUTOMATION.ACTION.KANBAN.REMOVE_AGENT'),
  },
  {
    key: 'add_checklist_item',
    label: t('AUTOMATION.ACTION.KANBAN.ADD_CHECKLIST_ITEM'),
  },
  {
    key: 'start_stage_timer',
    label: t('AUTOMATION.ACTION.KANBAN.START_STAGE_TIMER'),
  },
  {
    key: 'pause_stage_timer',
    label: t('AUTOMATION.ACTION.KANBAN.PAUSE_STAGE_TIMER'),
  },
];

// Local state
const actionType = ref(props.modelValue?.action_type || 'add_to_funnel');
const funnelId = ref(props.modelValue?.funnel_id || null);
const stageName = ref(props.modelValue?.stage_name || '');
const agentId = ref(props.modelValue?.agent_id || null);
const agentRole = ref(props.modelValue?.role || 'secondary');
const checklistItemText = ref(props.modelValue?.item_text || '');
const funnels = ref([]);
const agents = ref([]);
const loading = ref(false);

// Load funnels (FIX: Remove accountId parameter)
const loadFunnels = async () => {
  try {
    loading.value = true;
    // Bug Fix: API call should be FunnelAPI.get() without parameters
    const response = await FunnelAPI.get();
    funnels.value = response.data || [];
  } catch (error) {
    // Silent fail - funnels will be empty array
    funnels.value = [];
  } finally {
    loading.value = false;
  }
};

// Load agents
const loadAgents = () => {
  agents.value = getters['agents/getAgents'].value || [];
};

// Get stages for selected funnel (FIX: Convert Hash to Array)
const stages = computed(() => {
  if (!funnelId.value) return [];
  const funnel = funnels.value.find(f => f.id === funnelId.value);

  // Bug Fix: stages is a Hash/Object, not an array
  // Convert: {"prospecting": {...}, "qualification": {...}} to array
  if (!funnel?.stages) return [];

  // Convert stages object to array and sort by position
  return Object.entries(funnel.stages)
    .map(([name, config]) => ({
      name,
      label: config.label || name,
      position: config.position || 999,
      color: config.color,
    }))
    .sort((a, b) => a.position - b.position);
});

// Agent roles
const AGENT_ROLES = [
  { key: 'primary', label: t('AUTOMATION.ACTION.KANBAN.ROLE_PRIMARY') },
  { key: 'secondary', label: t('AUTOMATION.ACTION.KANBAN.ROLE_SECONDARY') },
];

// Determine which fields to show based on action type
const showFunnelSelect = computed(() => {
  return ['add_to_funnel', 'move_to_stage'].includes(actionType.value);
});

const showStageSelect = computed(() => {
  return ['move_to_stage'].includes(actionType.value);
});

const showStageOptional = computed(() => {
  return ['add_to_funnel'].includes(actionType.value);
});

const showAgentSelect = computed(() => {
  return ['assign_funnel_agent', 'remove_funnel_agent'].includes(
    actionType.value
  );
});

const showAgentRole = computed(() => {
  return ['assign_funnel_agent'].includes(actionType.value);
});

const showChecklistInput = computed(() => {
  return ['add_checklist_item'].includes(actionType.value);
});

const showNoConfig = computed(() => {
  return [
    'remove_from_funnel',
    'move_to_next_stage',
    'move_to_previous_stage',
    'start_stage_timer',
    'pause_stage_timer',
  ].includes(actionType.value);
});

// Watch for changes and emit
watch(
  [actionType, funnelId, stageName, agentId, agentRole, checklistItemText],
  () => {
    const value = {
      action_type: actionType.value,
    };

    if (showFunnelSelect.value && funnelId.value) {
      value.funnel_id = funnelId.value;
    }

    if ((showStageSelect.value || showStageOptional.value) && stageName.value) {
      value.stage_name = stageName.value;
    }

    if (showAgentSelect.value && agentId.value) {
      value.agent_id = agentId.value;
    }

    if (showAgentRole.value && agentRole.value) {
      value.role = agentRole.value;
    }

    if (showChecklistInput.value && checklistItemText.value) {
      value.item_text = checklistItemText.value;
    }

    emit('update:modelValue', value);
  },
  { deep: true }
);

// Initialize from props
onMounted(async () => {
  await loadFunnels();
  loadAgents();

  if (props.modelValue) {
    actionType.value = props.modelValue.action_type || 'add_to_funnel';
    funnelId.value = props.modelValue.funnel_id || null;
    stageName.value = props.modelValue.stage_name || '';
    agentId.value = props.modelValue.agent_id || null;
    agentRole.value = props.modelValue.role || 'secondary';
    checklistItemText.value = props.modelValue.item_text || '';
  }
});

// Handle action type change - reset dependent fields
const handleActionTypeChange = () => {
  if (!showFunnelSelect.value) {
    funnelId.value = null;
  }
  if (!showStageSelect.value && !showStageOptional.value) {
    stageName.value = '';
  }
  if (!showAgentSelect.value) {
    agentId.value = null;
  }
  if (!showAgentRole.value) {
    agentRole.value = 'secondary';
  }
  if (!showChecklistInput.value) {
    checklistItemText.value = '';
  }
};
</script>

<template>
  <div class="kanban-action-config space-y-3 mt-3">
    <!-- Action Type Select -->
    <div class="field">
      <label class="block text-sm font-medium text-n-strong mb-1">
        {{ t('AUTOMATION.ACTION.KANBAN.ACTION_TYPE') }}
      </label>
      <select
        v-model="actionType"
        class="w-full"
        @change="handleActionTypeChange"
      >
        <option
          v-for="action in KANBAN_ACTIONS"
          :key="action.key"
          :value="action.key"
        >
          {{ action.label }}
        </option>
      </select>
    </div>

    <!-- Kanban Select (for add_to_funnel and move_to_stage) -->
    <div v-if="showFunnelSelect" class="field">
      <label class="block text-sm font-medium text-n-strong mb-1">
        {{ t('AUTOMATION.ACTION.KANBAN.KANBAN') }}
        <span v-if="!showStageOptional" class="text-n-ruby-9">*</span>
      </label>
      <select v-model="funnelId" class="w-full" :required="showStageSelect">
        <option :value="null" disabled>
          {{ t('AUTOMATION.ACTION.KANBAN.SELECT_KANBAN') }}
        </option>
        <option v-for="funnel in funnels" :key="funnel.id" :value="funnel.id">
          {{ funnel.name }}
        </option>
      </select>
    </div>

    <!-- Stage Select (for move_to_stage) -->
    <div v-if="showStageSelect && funnelId" class="field">
      <label class="block text-sm font-medium text-n-strong mb-1">
        {{ t('AUTOMATION.ACTION.KANBAN.STAGE') }}
        <span class="text-n-ruby-9">*</span>
      </label>
      <select v-model="stageName" class="w-full" required>
        <option value="" disabled>
          {{ t('AUTOMATION.ACTION.KANBAN.SELECT_STAGE') }}
        </option>
        <option v-for="stage in stages" :key="stage.name" :value="stage.name">
          {{ stage.label }}
        </option>
      </select>
    </div>

    <!-- Stage Optional (for add_to_funnel) -->
    <div v-if="showStageOptional && funnelId" class="field">
      <label class="block text-sm font-medium text-n-strong mb-1">
        {{ t('AUTOMATION.ACTION.KANBAN.STAGE_OPTIONAL') }}
      </label>
      <select v-model="stageName" class="w-full">
        <option value="">
          {{ t('AUTOMATION.ACTION.KANBAN.USE_DEFAULT_STAGE') }}
        </option>
        <option v-for="stage in stages" :key="stage.name" :value="stage.name">
          {{ stage.label }}
        </option>
      </select>
      <p class="text-xs text-n-weak mt-1">
        {{ t('AUTOMATION.ACTION.KANBAN.STAGE_OPTIONAL_HINT') }}
      </p>
    </div>

    <!-- Agent Select (for assign_funnel_agent and remove_funnel_agent) -->
    <div v-if="showAgentSelect" class="field">
      <label class="block text-sm font-medium text-n-strong mb-1">
        {{ t('AUTOMATION.ACTION.KANBAN.AGENT') }}
        <span class="text-n-ruby-9">*</span>
      </label>
      <select v-model="agentId" class="w-full" required>
        <option :value="null" disabled>
          {{ t('AUTOMATION.ACTION.KANBAN.SELECT_AGENT') }}
        </option>
        <option v-for="agent in agents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
    </div>

    <!-- Agent Role (for assign_funnel_agent only) -->
    <div v-if="showAgentRole" class="field">
      <label class="block text-sm font-medium text-n-strong mb-1">
        {{ t('AUTOMATION.ACTION.KANBAN.ROLE') }}
      </label>
      <select v-model="agentRole" class="w-full">
        <option v-for="role in AGENT_ROLES" :key="role.key" :value="role.key">
          {{ role.label }}
        </option>
      </select>
    </div>

    <!-- Checklist Item Text (for add_checklist_item) -->
    <div v-if="showChecklistInput" class="field">
      <label class="block text-sm font-medium text-n-strong mb-1">
        {{ t('AUTOMATION.ACTION.KANBAN.CHECKLIST_ITEM') }}
        <span class="text-n-ruby-9">*</span>
      </label>
      <input
        v-model="checklistItemText"
        type="text"
        class="w-full"
        :placeholder="t('AUTOMATION.ACTION.KANBAN.CHECKLIST_ITEM_PLACEHOLDER')"
        required
      />
    </div>

    <!-- Info message for actions that don't need extra fields -->
    <div
      v-if="showNoConfig"
      class="info-message p-3 bg-n-blue-8/10 border border-n-blue-5 rounded-md"
    >
      <p class="text-sm text-n-blue-9">
        <template v-if="actionType === 'remove_from_funnel'">
          {{ t('AUTOMATION.ACTION.KANBAN.INFO.REMOVE_FROM_KANBAN') }}
        </template>
        <template v-if="actionType === 'move_to_next_stage'">
          {{ t('AUTOMATION.ACTION.KANBAN.INFO.MOVE_TO_NEXT_STAGE') }}
        </template>
        <template v-if="actionType === 'move_to_previous_stage'">
          {{ t('AUTOMATION.ACTION.KANBAN.INFO.MOVE_TO_PREVIOUS_STAGE') }}
        </template>
        <template v-if="actionType === 'start_stage_timer'">
          {{ t('AUTOMATION.ACTION.KANBAN.INFO.START_STAGE_TIMER') }}
        </template>
        <template v-if="actionType === 'pause_stage_timer'">
          {{ t('AUTOMATION.ACTION.KANBAN.INFO.PAUSE_STAGE_TIMER') }}
        </template>
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.kanban-action-config {
  select {
    @apply mb-0;
  }
}
</style>
